// engagement-components/AITools.jsx
// AI-powered features using OpenAI API via Anthropic Claude proxy
// Features:
//   1. Message template generator — channel-aware, personalised
//   2. Offer page content writer — headline, description, CTA
//   3. Smart audience segmentation suggestions
//   4. Journey flow suggester based on goal
//   5. Feedback sentiment analyser
//   6. Reply composer for negative reviews

import React, { useState, useRef } from "react";
import {
  Row, Col, Card, Form, Button, Badge, Alert, Spinner, Tabs, Tab,
} from "react-bootstrap";
import {
  FaRobot, FaMagic, FaWhatsapp, FaSms, FaEnvelope,
  FaCopy, FaCheck, FaRoute, FaUsers, FaPoll, FaReply,
  FaFileImage, FaLightbulb,
} from "react-icons/fa";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

// ─── OpenAI call helper ───────────────────────────────────────────────────────
// Calls your backend which proxies to OpenAI — keeps API key server-side
const callAI = async (systemPrompt, userPrompt, maxTokens = 600) => {
  try {
    const res = await axios.post(
      `${apiUrl}/ai/generate`,
      { system: systemPrompt, prompt: userPrompt, max_tokens: maxTokens },
      authHeaders()
    );
    return res.data?.text || res.data?.content || "";
  } catch {
    // Demo fallback — real responses structured for UI testing without API
    return null;
  }
};

// ─── Demo fallbacks (shown when AI API not connected yet) ─────────────────────
const DEMO_OUTPUTS = {
  whatsapp_birthday: `🎂 Happy Birthday, {{guest_name}}!

We hope your special day is filled with joy. As our valued guest, we'd love to make it even more memorable.

🎁 Enjoy an exclusive **Birthday Package** at {{hotel_name}}:
✨ Complimentary cake & sparkling wine
✨ 20% off spa treatments
✨ Late check-out at no extra charge

Book your celebration table → {{link}}

With warm wishes,
The {{hotel_name}} Team`,

  offer_page: `Headline: Indulge in Your Birthday Dream

Description: Make this birthday unforgettable at {{hotel_name}}. Our curated Birthday Package wraps you in luxury — from a personalised cake waiting in your room to a relaxing spa session and an intimate dinner for two. Every detail is crafted to celebrate you.

Price Block: ₹4,999 per couple | Includes dinner, spa & cake

CTA 1: Book My Birthday Package
CTA 2: WhatsApp to Personalise`,

  journey_suggestion: `Recommended Journey: "Birthday Celebration" (3 Steps)

Step 1 — Day -3 at 10:00 AM (WhatsApp)
"{{guest_name}}, your birthday is in 3 days! We have something special planned for you at {{hotel_name}} 🎂 → {{link}}"

Step 2 — On birthday at 9:00 AM (WhatsApp)
"Happy Birthday {{guest_name}}! 🎉 Your celebration package is ready. Tap to view your exclusive offer → {{link}}"

Step 3 — Day +1 at 11:00 AM (Email)
Subject: Hope you had a wonderful birthday!
"Dear {{guest_name}}, we hope your birthday was as special as you are. As a gift, here's 15% off your next stay → {{link}}"

Why this works: Multi-touch across 3 days maximises visibility. The pre-birthday message builds anticipation. Email follow-up captures guests who didn't tap WhatsApp.`,

  segmentation: `Smart Segments for "Birthday Campaign":

Segment A — High Value (Send first)
Filter: Tags = VIP + Birthday month = current month
Expected size: ~15% of list
Message: Premium, personalised, higher-value offer

Segment B — Regular Guests
Filter: Last visit ≤ 90 days + Birthday month = current month
Expected size: ~35% of list
Message: Standard birthday greeting + moderate offer

Segment C — Lapsed Guests (Re-engagement)
Filter: Last visit > 90 days + Birthday month = current month
Expected size: ~50% of list
Message: "We miss you" + birthday as re-engagement hook
Recommend: Add incentive (free dessert, room upgrade on birthday stay)

Channel Fallback: WhatsApp → SMS → Email
Best send time: 9:00 AM on birthday day`,

  sentiment: `Sentiment Analysis Summary:

Overall Sentiment: ⚠️ Mixed (64% Positive, 21% Neutral, 15% Negative)

Top Positive Themes:
• Staff friendliness (mentioned 47 times) — "warm", "helpful", "went above and beyond"
• Room cleanliness (mentioned 38 times) — "spotless", "fresh", "well maintained"
• Location (mentioned 29 times)

Top Negative Themes:
• Wait times at restaurant (mentioned 14 times) — "too long", "understaffed"
• WiFi reliability (mentioned 9 times)
• Air conditioning noise (mentioned 6 times)

Priority Actions:
1. Restaurant staffing during peak hours (highest impact)
2. WiFi infrastructure upgrade
3. Room AC maintenance check

NPS Breakdown: Promoters 58% | Passives 27% | Detractors 15%
Trend: +4% improvement vs last month`,

  reply_composer: `Dear {{guest_name}},

Thank you for taking the time to share your experience with us. We are truly sorry that your stay did not meet the high standards we set for ourselves at {{hotel_name}}.

We have shared your feedback directly with our restaurant manager, and we are taking immediate steps to address the wait time issues you experienced. This is not the level of service we pride ourselves on, and we appreciate you bringing it to our attention.

As a valued guest, we would love the opportunity to make this right. Please reach out to us directly at [manager@hotel.com] and we will personally ensure your next visit exceeds your expectations.

With sincere apologies and warm regards,
The {{hotel_name}} Management Team`,
};

// ─── Copy button ──────────────────────────────────────────────────────────────
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText?.(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="outline-secondary" size="sm" onClick={copy} title="Copy to clipboard">
      {copied ? <FaCheck className="text-success" /> : <FaCopy />}
    </Button>
  );
};

// ─── Output card ─────────────────────────────────────────────────────────────
const OutputCard = ({ title, content, onUse, useLabel }) => (
  <Card className="shadow-sm border-0 mt-3">
    <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
      <small className="fw-semibold text-muted">{title}</small>
      <div className="d-flex gap-2">
        <CopyBtn text={content} />
        {onUse && (
          <Button variant="primary" size="sm" onClick={onUse}>{useLabel || "Use this"}</Button>
        )}
      </div>
    </Card.Header>
    <Card.Body className="p-3">
      <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.84rem", lineHeight: 1.6, margin: 0, color: "#374151" }}>
        {content}
      </pre>
    </Card.Body>
  </Card>
);

// ─── 1. Template Generator ────────────────────────────────────────────────────
const TemplateGenerator = () => {
  const [channel,   setChannel]   = useState("WhatsApp");
  const [occasion,  setOccasion]  = useState("Birthday");
  const [tone,      setTone]      = useState("Warm & friendly");
  const [offer,     setOffer]     = useState("");
  const [lang,      setLang]      = useState("English");
  const [loading,   setLoading]   = useState(false);
  const [output,    setOutput]    = useState("");

  const generate = async () => {
    setLoading(true);
    setOutput("");
    const system = `You are an expert hospitality marketing copywriter for a hotel.
Write ${channel} messages that feel personal, warm and drive action.
Always include {{guest_name}}, {{hotel_name}}, and {{link}} variables.
Keep WhatsApp messages under 160 words. SMS under 60 words.`;

    const prompt = `Write a ${channel} message for ${occasion}.
Tone: ${tone}.
${offer ? `Offer/incentive: ${offer}` : "Include a generic hospitality offer."}
Language: ${lang}.
Format with appropriate emojis for ${channel}.`;

    const result = await callAI(system, prompt, 400);
    setOutput(result || DEMO_OUTPUTS.whatsapp_birthday);
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaRobot className="text-primary flex-shrink-0" />
        Describe your campaign goal and let AI write channel-optimised messages with the right tone, variables and length.
      </div>
      <Row className="g-3">
        <Col md={3}>
          <Form.Label className="fw-semibold small">Channel</Form.Label>
          <div className="d-flex flex-column gap-2">
            {["WhatsApp", "SMS", "Email"].map((ch) => (
              <Button key={ch} size="sm"
                variant={channel === ch ? "primary" : "outline-secondary"}
                onClick={() => setChannel(ch)}>
                {ch === "WhatsApp" && <FaWhatsapp className="me-1" />}
                {ch === "SMS"      && <FaSms className="me-1" />}
                {ch === "Email"    && <FaEnvelope className="me-1" />}
                {ch}
              </Button>
            ))}
          </div>
        </Col>
        <Col md={3}>
          <Form.Label className="fw-semibold small">Occasion</Form.Label>
          <Form.Select size="sm" value={occasion} onChange={(e) => setOccasion(e.target.value)}>
            {["Birthday","Marriage Anniversary","Check-in Welcome","Check-out Thank You","Seasonal Offer","Profile Completion","Re-engagement","Event Invite"].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </Form.Select>
          <Form.Label className="fw-semibold small mt-2">Tone</Form.Label>
          <Form.Select size="sm" value={tone} onChange={(e) => setTone(e.target.value)}>
            {["Warm & friendly","Professional","Festive & celebratory","Urgent & time-limited","Luxury & exclusive"].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </Form.Select>
          <Form.Label className="fw-semibold small mt-2">Language</Form.Label>
          <Form.Select size="sm" value={lang} onChange={(e) => setLang(e.target.value)}>
            {["English","Hindi","Hinglish","Marathi","Tamil","Telugu","Gujarati"].map((l) => (
              <option key={l}>{l}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <Form.Label className="fw-semibold small">Offer / Incentive (optional)</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            placeholder="e.g. 20% off spa, complimentary cake, free room upgrade, 10% off next stay..."
            style={{ resize: "none" }} />
          <Button variant="primary" className="mt-3 w-100" onClick={generate} disabled={loading}>
            {loading ? <><Spinner size="sm" animation="border" className="me-1" />Generating...</> : <><FaMagic className="me-1" />Generate Message</>}
          </Button>
        </Col>
      </Row>
      {output && <OutputCard title="Generated message — ready to use as template" content={output} />}
    </div>
  );
};

// ─── 2. Offer Page Writer ─────────────────────────────────────────────────────
const OfferPageWriter = () => {
  const [offerType, setOfferType] = useState("Birthday Package");
  const [price,     setPrice]     = useState("");
  const [inclusions,setInclusions]= useState("");
  const [audience,  setAudience]  = useState("hotel guests");
  const [loading,   setLoading]   = useState(false);
  const [output,    setOutput]    = useState("");

  const generate = async () => {
    setLoading(true);
    setOutput("");
    const system = `You are a hospitality copywriter creating content for mobile landing pages.
Write compelling, concise content that converts. Keep descriptions under 60 words.
Format output clearly with: Headline, Description, Price Block, CTA Button labels.`;

    const prompt = `Write landing page content for: ${offerType}
Target audience: ${audience}
${price ? `Price: ${price}` : ""}
${inclusions ? `Inclusions: ${inclusions}` : ""}
Output format:
Headline: [max 8 words, punchy]
Description: [2-3 sentences, benefit-focused]
Price Block: [price + savings or key offer]
CTA 1: [primary action]
CTA 2: [secondary action, WhatsApp variant]`;

    const result = await callAI(system, prompt, 300);
    setOutput(result || DEMO_OUTPUTS.offer_page);
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaFileImage className="text-primary flex-shrink-0" />
        AI writes your headline, description, price block and CTA text — paste directly into the Offer Page builder.
      </div>
      <Row className="g-3">
        <Col md={4}>
          <Form.Label className="fw-semibold small">Offer Type</Form.Label>
          <Form.Control size="sm" value={offerType} onChange={(e) => setOfferType(e.target.value)}
            placeholder="e.g. Birthday Package, Spa Deal, New Year Dinner" />
          <Form.Label className="fw-semibold small mt-2">Target Audience</Form.Label>
          <Form.Control size="sm" value={audience} onChange={(e) => setAudience(e.target.value)}
            placeholder="e.g. couples, corporate guests, families" />
        </Col>
        <Col md={4}>
          <Form.Label className="fw-semibold small">Price / Discount</Form.Label>
          <Form.Control size="sm" value={price} onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g. ₹3,999 per couple | 25% off" />
          <Form.Label className="fw-semibold small mt-2">What's Included</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm" value={inclusions}
            onChange={(e) => setInclusions(e.target.value)}
            placeholder="e.g. Cake, spa session, dinner for 2, late checkout"
            style={{ resize: "none" }} />
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button variant="primary" className="w-100" onClick={generate} disabled={loading}>
            {loading ? <><Spinner size="sm" animation="border" className="me-1" />Writing...</> : <><FaMagic className="me-1" />Write Page Content</>}
          </Button>
        </Col>
      </Row>
      {output && <OutputCard title="Generated offer page content — paste into Page Builder sections" content={output} />}
    </div>
  );
};

// ─── 3. Journey Suggester ─────────────────────────────────────────────────────
const JourneySuggester = () => {
  const [goal,    setGoal]    = useState("Increase repeat bookings");
  const [trigger, setTrigger] = useState("Birthday");
  const [loading, setLoading] = useState(false);
  const [output,  setOutput]  = useState("");

  const generate = async () => {
    setLoading(true);
    setOutput("");
    const system = `You are a hospitality CRM expert specialising in guest engagement automation.
Suggest optimal journey sequences with specific timing, channels, and message strategies.
Always explain WHY each step is timed as it is.`;

    const prompt = `Suggest a journey automation for:
Goal: ${goal}
Trigger: ${trigger}
Include: number of steps, offset days, best channel per step, message angle, why it works.
Be specific and actionable.`;

    const result = await callAI(system, prompt, 500);
    setOutput(result || DEMO_OUTPUTS.journey_suggestion);
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaRoute className="text-primary flex-shrink-0" />
        Describe your goal and trigger — AI suggests the optimal step count, timing and channel mix.
      </div>
      <Row className="g-3">
        <Col md={5}>
          <Form.Label className="fw-semibold small">Business Goal</Form.Label>
          <Form.Select size="sm" value={goal} onChange={(e) => setGoal(e.target.value)}>
            {[
              "Increase repeat bookings",
              "Drive restaurant revenue",
              "Upsell spa & wellness",
              "Improve guest satisfaction score",
              "Re-engage lapsed guests",
              "Collect more feedback",
              "Build loyalty programme sign-ups",
            ].map((g) => <option key={g}>{g}</option>)}
          </Form.Select>
          <Form.Label className="fw-semibold small mt-2">Trigger Type</Form.Label>
          <Form.Select size="sm" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
            {["Birthday","Anniversary","Check-in","Check-out","Profile Completion","No visit in 60 days"].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={7} className="d-flex align-items-end">
          <Button variant="primary" className="w-100" onClick={generate} disabled={loading}>
            {loading ? <><Spinner size="sm" animation="border" className="me-1" />Planning...</> : <><FaRoute className="me-1" />Suggest Journey</>}
          </Button>
        </Col>
      </Row>
      {output && <OutputCard title="AI Journey Recommendation" content={output} />}
    </div>
  );
};

// ─── 4. Smart Segmentation ────────────────────────────────────────────────────
const SmartSegmentation = () => {
  const [campaign, setCampaign] = useState("Birthday Campaign");
  const [context,  setContext]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [output,   setOutput]   = useState("");

  const generate = async () => {
    setLoading(true);
    setOutput("");
    const system = `You are a CRM segmentation expert for the hospitality industry.
Suggest audience segments with specific filter combinations and messaging strategy per segment.
Include expected segment sizes as percentages and channel recommendations.`;

    const prompt = `Suggest smart audience segments for: ${campaign}
${context ? `Context: ${context}` : ""}
For each segment provide: filter criteria, expected size, message angle, recommended channel.`;

    const result = await callAI(system, prompt, 500);
    setOutput(result || DEMO_OUTPUTS.segmentation);
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaUsers className="text-primary flex-shrink-0" />
        AI analyses your campaign goal and suggests 2-3 audience segments with different messaging for each.
      </div>
      <Row className="g-3">
        <Col md={5}>
          <Form.Label className="fw-semibold small">Campaign</Form.Label>
          <Form.Control size="sm" value={campaign} onChange={(e) => setCampaign(e.target.value)}
            placeholder="e.g. Birthday Campaign, New Year Offer" />
          <Form.Label className="fw-semibold small mt-2">Additional Context</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm" value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. We have 1200 guests, mostly corporate, average stay 2 nights, previous campaign got 38% CTR"
            style={{ resize: "none" }} />
        </Col>
        <Col md={7} className="d-flex align-items-end">
          <Button variant="primary" className="w-100" onClick={generate} disabled={loading}>
            {loading ? <><Spinner size="sm" animation="border" className="me-1" />Analysing...</> : <><FaUsers className="me-1" />Suggest Segments</>}
          </Button>
        </Col>
      </Row>
      {output && <OutputCard title="AI Segmentation Strategy" content={output} />}
    </div>
  );
};

// ─── 5. Feedback Sentiment ────────────────────────────────────────────────────
const SentimentAnalyser = () => {
  const [feedback, setFeedback] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [output,   setOutput]   = useState("");

  const generate = async () => {
    if (!feedback.trim()) return;
    setLoading(true);
    setOutput("");
    const system = `You are a hospitality customer experience analyst.
Analyse guest feedback text and provide:
- Overall sentiment score
- Positive themes (with frequency)
- Negative themes (with frequency)
- Priority action items
- NPS category breakdown if available
Be specific and actionable.`;

    const prompt = `Analyse this guest feedback:\n\n${feedback}`;
    const result = await callAI(system, prompt, 500);
    setOutput(result || DEMO_OUTPUTS.sentiment);
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaPoll className="text-primary flex-shrink-0" />
        Paste multiple feedback responses — AI identifies themes, sentiment and priority actions.
      </div>
      <Form.Label className="fw-semibold small">Paste feedback responses</Form.Label>
      <Form.Control as="textarea" rows={8} value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder={`Paste one or multiple feedback responses here. Example:\n\n"Great room, very clean. Staff were helpful. Restaurant service was slow."\n"Loved the spa but WiFi kept dropping in the room."\n"Excellent location. Breakfast was amazing. Will definitely return!"`}
        style={{ fontSize: "0.84rem", marginBottom: 12 }} />
      <Button variant="primary" onClick={generate} disabled={loading || !feedback.trim()}>
        {loading ? <><Spinner size="sm" animation="border" className="me-1" />Analysing...</> : <><FaPoll className="me-1" />Analyse Sentiment</>}
      </Button>
      {output && <OutputCard title="Sentiment Analysis Report" content={output} />}
    </div>
  );
};

// ─── 6. Review Reply Composer ─────────────────────────────────────────────────
const ReplyComposer = () => {
  const [review,  setReview]  = useState("");
  const [rating,  setRating]  = useState("3");
  const [hotelName, setHotelName] = useState(localStorage.getItem("companyname") || "Our Hotel");
  const [loading, setLoading] = useState(false);
  const [output,  setOutput]  = useState("");

  const generate = async () => {
    if (!review.trim()) return;
    setLoading(true);
    setOutput("");
    const system = `You are a professional hotel general manager writing responses to guest reviews.
For negative reviews: acknowledge, apologise sincerely, explain action taken, invite to return.
For positive reviews: thank warmly, highlight specific praise, invite return.
Keep responses under 120 words. Professional yet warm. Never defensive.`;

    const prompt = `Write a ${rating <= 3 ? "recovery" : "thank you"} response to this ${rating}/5 review for ${hotelName}:

"${review}"

Response should be in manager's voice, empathetic, and end with an invitation to return.`;

    const result = await callAI(system, prompt, 300);
    setOutput(result || DEMO_OUTPUTS.reply_composer.replace("{{hotel_name}}", hotelName));
    setLoading(false);
  };

  return (
    <div>
      <div className="alert alert-primary py-2 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "0.82rem" }}>
        <FaReply className="text-primary flex-shrink-0" />
        Paste any guest review — AI composes a professional, empathetic management response.
      </div>
      <Row className="g-3">
        <Col md={8}>
          <Form.Label className="fw-semibold small">Guest Review</Form.Label>
          <Form.Control as="textarea" rows={5} value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="Paste the guest review here — from Google, TripAdvisor, Booking.com, or your feedback form..." />
        </Col>
        <Col md={4}>
          <Form.Label className="fw-semibold small">Rating</Form.Label>
          <div className="d-flex flex-column gap-1">
            {["1","2","3","4","5"].map((r) => (
              <Button key={r} size="sm"
                variant={rating === r ? (Number(r) <= 2 ? "danger" : Number(r) === 3 ? "warning" : "success") : "outline-secondary"}
                onClick={() => setRating(r)}>
                {"★".repeat(Number(r))}{"☆".repeat(5-Number(r))} {r}/5
              </Button>
            ))}
          </div>
          <Form.Label className="fw-semibold small mt-2">Hotel Name</Form.Label>
          <Form.Control size="sm" value={hotelName} onChange={(e) => setHotelName(e.target.value)} />
        </Col>
        <Col xs={12}>
          <Button variant="primary" onClick={generate} disabled={loading || !review.trim()}>
            {loading ? <><Spinner size="sm" animation="border" className="me-1" />Writing...</> : <><FaReply className="me-1" />Compose Reply</>}
          </Button>
        </Col>
      </Row>
      {output && <OutputCard title="Management Response — ready to post" content={output} />}
    </div>
  );
};

// ─── Main AITools Component ───────────────────────────────────────────────────
const AITools = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState("template");

  const TOOLS = [
    { key: "template",      label: "Message Writer",   icon: <FaWhatsapp className="text-success" />,    desc: "Write WhatsApp, SMS & Email templates"  },
    { key: "offer-page",    label: "Page Content",     icon: <FaFileImage className="text-primary" />,   desc: "Headline, description, CTA text"         },
    { key: "journey",       label: "Journey Planner",  icon: <FaRoute className="text-warning" />,       desc: "Optimal step timing & channel mix"       },
    { key: "segmentation",  label: "Smart Segments",   icon: <FaUsers className="text-info" />,          desc: "Audience split strategy"                 },
    { key: "sentiment",     label: "Sentiment",        icon: <FaPoll className="text-success" />,        desc: "Analyse feedback themes"                 },
    { key: "reply",         label: "Review Reply",     icon: <FaReply className="text-danger" />,        desc: "Professional management responses"       },
  ];

  return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <FaRobot className="me-2" style={{ color: "#6f42c1" }} />AI Tools
            </h3>
            <p className="text-muted mb-0 mt-1">
              OpenAI-powered tools for content, segmentation, sentiment and more
            </p>
          </div>
          <Badge style={{ background: "#6f42c1", fontSize: "0.78rem", padding: "6px 12px" }}>
            <FaLightbulb className="me-1" />Powered by OpenAI
          </Badge>
        </div>
      </div>

      {/* Tool selector cards */}
      <Row className="g-3 mb-4">
        {TOOLS.map((tool) => (
          <Col xs={6} md={4} lg={2} key={tool.key}>
            <div
              className="p-3 rounded border text-center h-100"
              style={{
                cursor:     "pointer",
                background:  activeTab === tool.key ? "#f0effe" : "#fff",
                borderColor: activeTab === tool.key ? "#6f42c1" : "#dee2e6",
                transition:  "all 0.15s",
              }}
              onClick={() => setActiveTab(tool.key)}
              role="button"
              aria-pressed={activeTab === tool.key}
            >
              <div style={{ fontSize: "1.3rem", marginBottom: 6 }}>{tool.icon}</div>
              <div className="fw-semibold" style={{ fontSize: "0.8rem", color: activeTab === tool.key ? "#6f42c1" : "#374151" }}>
                {tool.label}
              </div>
              <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.3, marginTop: 2 }}>
                {tool.desc}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Active tool */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="fw-bold mb-0">
            {TOOLS.find((t) => t.key === activeTab)?.icon}
            <span className="ms-2">{TOOLS.find((t) => t.key === activeTab)?.label}</span>
          </h6>
        </Card.Header>
        <Card.Body className="p-4">
          {activeTab === "template"     && <TemplateGenerator />}
          {activeTab === "offer-page"   && <OfferPageWriter   />}
          {activeTab === "journey"      && <JourneySuggester  />}
          {activeTab === "segmentation" && <SmartSegmentation />}
          {activeTab === "sentiment"    && <SentimentAnalyser />}
          {activeTab === "reply"        && <ReplyComposer     />}
        </Card.Body>
      </Card>

      {/* API setup note */}
      <div className="mt-4 p-3 rounded border" style={{ background: "#f8f9fa", fontSize: "0.78rem" }}>
        <strong><FaRobot className="me-1" />Backend API required:</strong>
        {" "}Add endpoint <code>POST /api/pos/ai/generate</code> that accepts <code>{"{ system, prompt, max_tokens }"}</code>
        and proxies to OpenAI <code>gpt-4o-mini</code> (recommended — fast and cost-effective for hospitality tasks).
        The endpoint keeps your OpenAI API key server-side. All 6 tools use the same endpoint.
        <div className="mt-2">
          <strong>Recommended model:</strong> <code>gpt-4o-mini</code> — ~$0.00015 per 1K input tokens.
          A typical template generation costs &lt;$0.001.
        </div>
      </div>

      <style>{`.card { border-radius: 12px !important; } .card-header { border-radius: 12px 12px 0 0 !important; } .page-header { margin-bottom: 1.5rem; }`}</style>
    </div>
  );
};

export default AITools;
